<template>
  <section class="containerSpotlightUploader" :class="{disabled: disabled, error: isThereAnError, required: required}" >
    <span class="containerSpotlightUploader__label" v-if="label">{{label}}</span>
    <section class="containerSpotlightUploader__dragArea"
             @dragenter="addHoverClass"
             @dragover="stopEvent"
             @dragleave="removeHoverClass"
             @drop="dropHandler"
             :class="{ active: hoverIsActive}"
             v-if="!fileInfo.status || fileInfo.status === 'failed'">
      <section class="containerSpotlightUploader__dragArea--main">
        <section class="containerLabels">
          <span class="grey-icon-upload-cloud"></span>
          <span class="containerLabels__label">Drop or </span>
          <button class="containerLabels__button" @click="openFileSelector" type="button">Select your File...</button>
        </section>
      </section>
      <input type="file"
             @change="inputChanged"
             ref="inputFileRef"
             class="containerSpotlightUploader__dragArea--input"
             :accept="config.validExtensions[fileType]"
             id="containerUpload"/>
    </section>
    <template v-else>
      <section v-if="previewImageLoaded || store.state.spotlight.spotlightItem" class="containerSpotlightUploader__fileTempPreview">
        <img v-if="tempImage" :src="tempImage.src" class="containerSpotlightUploader__fileTempPreview--image">
        <img v-else :src="store.state.spotlight.spotlightItem.imageUrl" class="containerSpotlightUploader__fileTempPreview--image">
      </section>
      <section class="containerSpotlightUploader__fileInfo" :class="{'failed': failedStatus}">
        <span class="containerSpotlightUploader__fileInfo--label" v-if="uploadingStatus">{{percentageLabel}}</span>
        <span class="containerSpotlightUploader__fileInfo--label" v-if="completedStatus">
          <span class="grey-icon-success-no-fill"></span>Upload successful</span>
        <span class="containerSpotlightUploader__fileInfo--label" v-if="failedStatus">Upload failed</span>
        <span class="containerSpotlightUploader__fileInfo--remove" @click="handleClick">Remove</span>
      </section>
    </template>
    <confirmationModal
      ref="deleteFileModalRef"
      class="deleteFileModal"
      title="Delete File"
      message="Are you sure you want to delete this file?"
      confirmText="DELETE"
      cancelText="CANCEL"
      @confirmAction="removeImage"
    />
    <confirmationModal
      ref="notSupportedModalRef"
      class="notSupportedModal"
      title="File Not Supported"
      message="Sorry, it looks like the selected file has an extension that we don't support."
      confirmText="OK"
      :singleButton="true"
    />
  </section>
</template>

<script>
import { useStore } from 'vuex'
import fileUploaderConfig from './validExtensions.json'
import confirmationModal from '@/components/confirmationModal/confirmationModal'
import { computed, ref } from 'vue'

export default {
  name: 'spotlightImageUploader',
  components: {
    confirmationModal
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    showErrors: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      required: false
    },
    fileType: {
      type: String,
      default: 'img'
    }
  },
  emits: ['touched-file', 'set-validation-active'],
  setup (props, { emit }) {
    const store = useStore()
    const hoverIsActive = ref(false)
    const config = fileUploaderConfig
    const counterHover = ref(0)
    const tempId = ref(null)
    const inputFileRef = ref(null)
    const deleteFileModalRef = ref(null)
    const notSupportedModalRef = ref(null)
    const tempImage = ref(null)
    const previewImageLoaded = ref(false)

    /**
     * @description Object with all the file information.
     */
    const fileInfo = computed(() => {
      return store.state.spotlight.fileInfo
    })

    /**
     * @description Flag to indicate if an error should be shown.
     */
    const isThereAnError = computed(() => {
      return (props.showErrors && (props.required && fileInfo.value.status !== 'completed')) || fileInfo.value.status === 'failed'
    })

    /**
     * @description Validate if file status is uploading.
     */
    const uploadingStatus = computed(() => {
      return fileInfo.value.status === 'uploading'
    })

    /**
     * @description Validate if file status is completed.
     */
    const completedStatus = computed(() => {
      return fileInfo.value.status === 'completed'
    })

    /**
     * @description Validate if file status is failed.
     */
    const failedStatus = computed(() => {
      return fileInfo.value.status === 'failed'
    })

    /**
     * @description Get percentage label.
     */
    const percentageLabel = computed(() => {
      return `${fileInfo.value.progress}% of ${fileInfo.value.size}`
    })

    /**
     * @description Opens the modal to select a file.
     */
    function openFileSelector () {
      inputFileRef.value.click()
    }

    /**
     * @description Adds class with stiles of hover on drag and drop area.
     * @param event event fired by user.
     */
    function addHoverClass (event) {
      toggleHoverClass(event, true)
    }

    /**
     * @description Removes class with stiles of hover on drag and drop area.
     * @param event event fired by user.
     */
    function removeHoverClass (event) {
      toggleHoverClass(event, false)
    }

    /**
     * @description Toggles precence of hover class to element based on user interaction.
     * @param event event fired by user.
     * @param isClassPresent flag to indicate if hover class should be applied or not.
     */
    function toggleHoverClass (event, isClassPresent) {
      stopEvent(event)
      if (props.disabled) return

      if (isClassPresent) {
        hoverIsActive.value = true
        counterHover.value++
      } else {
        counterHover.value--
        if (counterHover.value === 0) {
          hoverIsActive.value = false
        }
      }
    }

    /**
     * @description Stops propagation of a given event.
     * @param {event} event to stop its propagation.
     */
    function stopEvent (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    /**
     * @description Manages the drop event to get the file and start to upload process.
     * @param {event} event launches by user's drop action.
     */
    function dropHandler (event) {
      stopEvent(event)

      if (props.disabled) return

      hoverIsActive.value = false

      if (event.dataTransfer && event.dataTransfer.files.length > 0) {
        setFile(event.dataTransfer.files[0])
      }
    }

    /**
     * @description Handler to read the file from the input.
     */
    function inputChanged () {
      setShowErrors(false)
      setFile(inputFileRef.value.files[0])
      emit('touched-file')
    }

    /**
     * @description Validates if a file has a valid extension to be uploaded.
     * @param {fileName} fileName to validate its extesion.
     */
    function isAValidExtension (fileName) {
      const fileNameSplitted = fileName.split('.')
      const extension = fileNameSplitted[fileNameSplitted.length - 1].toLowerCase()
      return config.validExtensions[props.fileType].split(',').some((ext) => ext.toLowerCase() === `.${extension}`)
    }

    /**
     * @description Uploads the file if it's valid.
     * @param file the file selected
     */
    function setFile (file) {
      if (!isAValidExtension(file.name)) {
        openModal(notSupportedModalRef.value)
        return
      }
      const reader = new FileReader()
      const image = new Image()

      readImage(file, reader, image).then(
        () => {
          getImage(image).then(
            (width, height) => {
              if (width < 800 || height < 800) {
                emit('set-validation-active', true)
                return
              }
              emit('set-validation-active', false)
              emit('touched-file')
              setTempPlaceholder(file)
              store.commit({
                type: 'spotlight/UPDATE_UPLOAD_SPOTLIGHT_LOGO_INFO',
                file: file
              })
              store.dispatch({
                type: 'spotlight/uploadSpotlightImage',
                file: file
              })
            }
          )
        }
      )
    }

    /**
     * @description Get dimensions of image
     * @param image object of JS
     * @returns {Promise} resolve promise
     */
    function getImage (image) {
      return new Promise((resolve, reject) => {
        image.onload = () => {
          const { width, height } = image
          resolve(width, height)
        }
      })
    }

    /**
     * @description Opens a modal
     * @param modalRef The modal to open
     */
    function openModal (modalRef) {
      modalRef.open()
    }

    /**
     * @descriptions Cancels the current upload process.
     */
    function removeImage () {
      store.commit({
        type: 'spotlight/SET_SPOTLIGHT_IMAGE_ID',
        id: ''
      })
      cleanFile()
    }

    /**
     * @description Set show errors
     * @param showErrors Boolean flag
     */
    function setShowErrors (showErrors = true) {
      store.commit({
        type: 'SET_SHOW_ERRORS',
        showErrors: showErrors
      })
    }

    /**
     * @description Set temporal placeholder for file uploader component
     * @param file selected file
     */
    function setTempPlaceholder (file) {
      const reader = new FileReader()
      tempImage.value = new Image()
      readImage(file, reader).then(
        () => {
          previewImageLoaded.value = true
        }
      )
    }

    /**
     * @description Read File (Image)
     * @param file uploaded
     * @param reader object of JS
     * @param image object of JS
     * @returns {Promise} resolve promise
     */
    function readImage (file, reader, image = tempImage.value) {
      return new Promise((resolve, reject) => {
        reader.onloadend = (e) => {
          image.src = reader.result
          resolve()
        }
        reader.readAsDataURL(file)
      })
    }

    /**
     * @description Handles click to open remove file modal when necessary
     */
    function handleClick () {
      if (failedStatus.value) {
        cleanFile()
      } else {
        deleteFileModalRef.value.open()
      }
    }

    /**
     * @description Cleans the file to allow upload a new file.
     */
    function cleanFile () {
      inputFileRef.value = null
      tempImage.value = null
      previewImageLoaded.value = false
      store.commit({
        type: 'spotlight/CLEAN_SPOTLIGHT_IMAGE_INFO'
      })
      emit('touched-file')
    }

    return {
      store,
      hoverIsActive,
      config,
      counterHover,
      tempId,
      fileInfo,
      isThereAnError,
      inputFileRef,
      openFileSelector,
      addHoverClass,
      removeHoverClass,
      toggleHoverClass,
      stopEvent,
      dropHandler,
      inputChanged,
      removeImage,
      deleteFileModalRef,
      notSupportedModalRef,
      openModal,
      tempImage,
      previewImageLoaded,
      uploadingStatus,
      completedStatus,
      failedStatus,
      percentageLabel,
      handleClick
    }
  }
}
</script>
